import React from 'react';
import {
  BrowserRouter, Route, Routes, useLocation, useMatch,
} from 'react-router-dom';
import { ReactQueryProvider } from '@/utils/ReactQuery';
import cn from '@/utils/cn';
import ContentRail from '../LegacyContentRail/ContentRail';
import { ContentMountProvider } from '../SysadminSPA/ContentView/contentMount';
import ProgramEditPage, { NewProgramPage, programEditRoutes } from '../SysadminSPA/ContentView/ProgramEdit';
import LessonGuidesPage from '../SysadminSPA/ContentView/LessonGuides';
import LessonSectionsPage from '../SysadminSPA/ContentView/LessonSections';
import SectionCopyModal from '../SysadminSPA/ContentView/LessonSections/SectionCopyModal';
import SectionStatesModal from '../SysadminSPA/ContentView/LessonSections/SectionStatesModal';
import PrintToolsPage from '../SysadminSPA/ContentView/PrintTools';
import LessonStatesModal from '../SysadminSPA/ContentView/ProgramLessons/LessonStatesModal';
import ProgramLessonsPage from '../SysadminSPA/ContentView/ProgramLessons/ProgramLessonsPage';
import ProgramUnitsPage from '../SysadminSPA/ContentView/ProgramLessons/ProgramUnitsPage';
import SharedProgramsModal from '../SysadminSPA/ContentView/ProgramLessons/SharedProgramsModal';
import SPAReuseLessonsModal from '../SysadminSPA/ContentView/ProgramLessons/SPAReuseLessonsModal';
import programsPageRoutes from '../SysadminSPA/ContentView/programsPageRoutes';
import UnitEditPage, { unitEditRoutes } from '../SysadminSPA/ContentView/UnitEdit';
import type { NavLeaf } from '../SysadminSPA/shared/navSections';
import { SPAUserProvider } from '../SysadminSPA/shared/SPAUser';
import { RailLinkProvider } from '../SysadminSPA/shared/ProgramTree/RailLink';
import { useProgramSummary } from '../SysadminSPA/shared/ProgramTree/usePrograms';
import { SPAThemeScope } from '../SysadminSPA/shared/SPAThemeScope';
import LegacyMountRailLink from './LegacyMountRailLink';

// AIDEV-NOTE: The SPA's Programs page (Standard/Custom programs, Alert Messages, Reference
// Materials tabs) mounted on the legacy /admin/programs page, the way admin/UserSearch and
// admin/AISafetyReport are. The legacy chrome is light, so the SPA tokens are pinned to their light
// set (.spa-theme-light); SPAThemeScope carries the class to the portalled dialogs and menus.
// The tabs and routed modals are child routes, so `admin/programs/*path` must route to #index for
// deep links and reloads (config/routes.rb); the new, edit and lessons URLs are their Rails actions,
// which render this page behind the flag. ContentMountProvider tells shared pages which mount they
// are on, so their links use this mount's paths (see programLessonsPath, programEditPath).
//
// The content rail is rendered here, inside this router, rather than by the layout: its links to
// this page's tabs are then router links and switch tabs without a reload, and its highlight
// follows. On a program route (the lessons or edit page) it drills into that program, with a router
// crumb back to the table; LegacyMountRailLink makes the drill's program pages router links too.
// The layout still reserves the rail's column (@content_rail = :in_page) and
// #sysadmin_content_rail's fixed positioning comes from _revamp.scss as for the layout's rail.
const THEME_CLASS = 'spa-theme-light';
// The rail is pinned dark beside the light page: the sysadmin's call, so it reads as one strong
// frame around the content.
const RAIL_THEME_CLASS = 'spa-theme-dark';
const BASE = '/admin/programs';

const groundClasses = cn(
  THEME_CLASS,
  'tw-box-border tw-bg-spa-bg tw-text-spa-on-bg',
  'tw-shadow-[0_0_0_100vmax_var(--spa-bg)]',
);

// Tree items that are this page's own tabs get a router path; everything else stays an anchor.
const tabPathFor = (item: NavLeaf) => {
  if (!item.legacyHref?.startsWith(BASE)) return null;

  return item.legacyHref.slice(BASE.length) || '/';
};

interface RailProps {
  canManageContent?: boolean;
  canManageSupport?: boolean;
  signOutPath: string;
  supportPath: string;
  userName: string;
}

const InPageRail = ({
  canManageContent, canManageSupport, signOutPath, supportPath, userName,
}: RailProps) => {
  const { pathname } = useLocation();
  const editMatch = useMatch('/:programId/edit/*');
  const lessonsMatch = useMatch('/:programId/lessons/*');
  const unitsMatch = useMatch('/:programId/units/*');
  const programId = Number((editMatch ?? lessonsMatch ?? unitsMatch)?.params.programId) || undefined;
  const program = useProgramSummary(programId ?? 0);

  return (
    <div className={RAIL_THEME_CLASS} id="sysadmin_content_rail">
      <SPAThemeScope className={RAIL_THEME_CLASS}>
        <div className="tw-h-full">
          <ContentRail
            backTo="/"
            canManageContent={canManageContent}
            canManageSupport={canManageSupport}
            currentPath={`${BASE}${pathname === '/' ? '' : pathname}`}
            linkTo={tabPathFor}
            programColor={program.color}
            programId={programId}
            programTitle={program.title}
            signOutPath={signOutPath}
            supportPath={supportPath}
            userName={userName}
          />
        </div>
      </SPAThemeScope>
    </div>
  );
};

interface ProgramsPageProps extends RailProps {
  showPrintTools?: boolean;
  withRail?: boolean;
}

const ProgramsPage = ({
  canManageContent = true,
  canManageSupport = true,
  showPrintTools = false,
  signOutPath,
  supportPath,
  userName,
  withRail = false,
}: ProgramsPageProps) => (
  <ReactQueryProvider>
    <SPAUserProvider value={{
      canManageContent, canManageSupport, showPrintTools, showSearch: false,
    }}
    >
      <BrowserRouter basename={BASE}>
        <RailLinkProvider value={LegacyMountRailLink}>
          {withRail && (
            <InPageRail
              canManageContent={canManageContent}
              canManageSupport={canManageSupport}
              signOutPath={signOutPath}
              supportPath={supportPath}
              userName={userName}
            />
          )}
          <SPAThemeScope className={THEME_CLASS}>
            <ContentMountProvider value="legacy">
              <div className={groundClasses} style={{ clipPath: 'inset(0 -100vmax)' }}>
                <Routes>
                  {programsPageRoutes}
                  {/* The Rails new, edit and lessons URLs, so each is a router link and a reload lands here. */}
                  <Route element={<NewProgramPage />} path="/new" />
                  <Route element={<ProgramEditPage />} path="/:programId/edit">{programEditRoutes}</Route>
                  <Route element={<LessonGuidesPage />} path="/:programId/lessons/:lessonId/guides" />
                  <Route element={<LessonSectionsPage />} path="/:programId/lessons/:lessonId/sections">
                    <Route element={<SectionCopyModal />} path=":sectionId/copy" />
                    <Route element={<SectionStatesModal />} path=":sectionId/states" />
                  </Route>
                  <Route element={<PrintToolsPage />} path="/:programId/lessons/:lessonId/print-tools" />
                  <Route element={<LessonGuidesPage />} path="/:programId/units/:unitId/guides" />
                  <Route element={<ProgramUnitsPage />} path="/:programId/units" />
                  <Route element={<UnitEditPage />} path="/:programId/units/:unitId/edit">{unitEditRoutes}</Route>
                  <Route element={<ProgramLessonsPage />} path="/:programId/lessons">
                    <Route element={<SPAReuseLessonsModal />} path="reuse-lessons" />
                    <Route element={<SharedProgramsModal />} path="lessons/:lessonId/shared-programs" />
                    <Route element={<LessonStatesModal />} path="lessons/:lessonId/states" />
                  </Route>
                </Routes>
              </div>
            </ContentMountProvider>
          </SPAThemeScope>
        </RailLinkProvider>
      </BrowserRouter>
    </SPAUserProvider>
  </ReactQueryProvider>
);

export default ProgramsPage;
